import React from "react";
import { formatDisplayDate, parseLocalDate } from "../../utils/dateUtils";
import { formatHours } from "../../utils/formatHours";
import "./EndDateBreakdown.css";

const TYPE_CONFIG = {
  WORKING: {
    bgColor: "#F0FDF4",
    textColor: "#15803D",
    icon: "",
    label: "Working day",
  },
  WORKING_RANGE: {
    bgColor: "#F0FDF4",
    textColor: "#15803D",
    icon: "",
    label: null,
  },
  HALF_DAY: {
    bgColor: "#F8FAFC",
    textColor: "#0F172A",
    icon: "",
    label: "Half day",
  },
  OFF: {
    bgColor: "#FEF2F2",
    textColor: "#B91C1C",
    icon: "",
    label: "Leave",
  },
  HOLIDAY: {
    bgColor: "#FFF7ED",
    textColor: "#C2410C",
    icon: "",
    label: "Public holiday",
  },
  WEEKLY_OFF: {
    bgColor: "#F9FAFB",
    textColor: "#6B7280",
    icon: "",
    label: "Weekly off",
  },
  AFTER_HOURS: {
    bgColor: "#FEF2F2",
    textColor: "#B91C1C",
    icon: "",
    label: "After hours",
  },
};

const DAY_LABELS = {
  MON: "Mon",
  TUE: "Tue",
  WED: "Wed",
  THU: "Thu",
  FRI: "Fri",
  SAT: "Sat",
  SUN: "Sun",
};

export default function EndDateBreakdown({ result, userName, workingDays }) {
  if (!result) return null;

  const { endDate, expectedEndDate, summary, collapsedBreakdown } = result;

  const formatDate = (expectedEndDate) => {
    if (!expectedEndDate) return "";
    return formatDisplayDate(expectedEndDate, {
      day: "numeric",
      month: "short",
    });
  };

  return (
    <div className="end-date-breakdown">
      <div className="end-date-headline">
        <span className="end-date-label">Expected completion</span>
        <span className="end-date-value">
          {formatDisplayDate(expectedEndDate, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })}
        </span>
      </div>

      <div className="schedule-context">
        <span className="context-label">
          Using {userName}'s schedule{" "}
          {(workingDays || []).map((d) => DAY_LABELS[d]).join(", ")}
          {result.workStartTime && result.workEndTime && (
            <>
              {" "}
              {result.workStartTime} - {result.workEndTime}
            </>
          )}
          {result.hoursPerDay && <> and {result.hoursPerDay}h/day</>}
        </span>
      </div>

      {/* {summary?.startedPartialDay && (
        <div className="partial-day-notice">
          Starting today with {formatHours(summary.hoursRemainingOnStartDay)}{" "}
          remaining in the working day
        </div>
      )} */}

      <div className="breakdown-list">
        {collapsedBreakdown.map((entry, index) => {
          const config = TYPE_CONFIG[entry.type] || TYPE_CONFIG.WORKING;

          if (entry.type === "WORKING_RANGE") {
            return (
              <div
                key={index}
                className="breakdown-row"
                style={{
                  backgroundColor: config.bgColor,
                  color: config.textColor,
                }}
              >
                <span className="breakdown-icon">{config.icon}</span>
                <span className="breakdown-dates">
                  {formatDate(entry.startDate)} {formatDate(entry.endDate)}
                </span>
                <span className="breakdown-label">{entry.label}</span>
              </div>
            );
          }

          if (entry.type === "WORKING") {
            return (
              <div
                key={index}
                className={`breakdown-row ${
                  entry.isEndDate ? "breakdown-row--end" : ""
                } ${entry.isPartialDay ? "breakdown-row--partial" : ""}`}
                style={{
                  backgroundColor: config.bgColor,
                  color: config.textColor,
                }}
              >
                <span className="breakdown-icon">{config.icon}</span>
                <span className="breakdown-dates">
                  {formatDisplayDate(parseLocalDate(entry.date), {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}{" "}
                  <span className="breakdown-dayname">{entry.dayName}</span>
                </span>
                <span className="breakdown-label">
                  {entry.isPartialDay
                    ? `Partial day  ${entry.label}`
                    : "Full working day"}
                  {entry.isEndDate && (
                    <span className="end-date-marker">ends here</span>
                  )}
                </span>
                <span className="breakdown-hours">
                  {formatHours(entry.hoursUsed)}
                </span>
              </div>
            );
          }

          return (
            <div
              key={index}
              className={`breakdown-row ${
                entry.isEndDate ? "breakdown-row--end" : ""
              }`}
              style={{
                backgroundColor: config.bgColor,
                color: config.textColor,
              }}
            >
              <span className="breakdown-icon">{config.icon}</span>
              <span className="breakdown-dates">
                {formatDate(entry.date)}{" "}
                <span className="breakdown-dayname">{entry.dayName}</span>
              </span>
              <span className="breakdown-label">
                {entry.label ? `${config.label}  ${entry.label}` : config.label}
                {entry.isEndDate && (
                  <span className="end-date-marker"> ? ends here</span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      <div className="breakdown-summary">
        <div className="breakdown-summary">
          <span>{formatHours(summary.durationHours)} of work</span>
          <span className="summary-sep"> + </span>
          <span>
            {summary.workingDaysCount} working day
            {summary.workingDaysCount !== 1 ? "s" : ""}
          </span>
          {summary.weeklyOffs > 0 && (
            <>
              <span className="summary-sep"> + </span>
              <span>
                {summary.weeklyOffs} weekend day
                {summary.weeklyOffs !== 1 ? "s" : ""}
              </span>
            </>
          )}
          {summary.holidays > 0 && (
            <>
              <span className="summary-sep"> + </span>
              <span>
                {summary.holidays} holiday{summary.holidays !== 1 ? "s" : ""}
              </span>
            </>
          )}
          <span className="summary-sep"> = </span>
          <strong>
            ends{" "}
            {formatDisplayDate(expectedEndDate, {
              weekday: "short",
              day: "numeric",
              month: "short",
            })}
          </strong>
        </div>
        {/* <strong>
          ends{" "}
          {(parseLocalDate(endDate) || new Date()).toLocaleDateString("en-IN", {
            weekday: "short",
            day: "numeric",
            month: "short",
          })}
        </strong> */}
      </div>
    </div>
  );
}
